/** @jsxImportSource @emotion/react */
import Fade from '@mui/material/Fade';
import Modal from '@mui/material/Modal';
import * as React from 'react';

import { contentBaseCss, dialogBaseCss, rootSx, sizeCss } from './styles';

const DEFAULT_Z_INDEX = 1050;
const MODAL_TRANSITION = 300;
const BACKDROP_TRANSITION = 150;

export const MODAL_CONTENT_TEST_ID = 'modal-content';

export interface ModalBaseProps {
  isOpen?: boolean;
  toggle?: (e?: React.SyntheticEvent) => void;
  size?: string;
  backdrop?: boolean | 'static';
  keyboard?: boolean;
  autoFocus?: boolean;
  returnFocusAfterClose?: boolean;
  unmountOnClose?: boolean;
  fade?: boolean;
  onOpened?: () => void;
  onClosed?: () => void;
  zIndex?: number | string;
  className?: string;
  contentClassName?: string;
  modalClassName?: string;
  backdropClassName?: string;
  backdropInvisible?: boolean;
  labelledBy?: string;
  role?: string;
  children?: React.ReactNode;
  'data-testid'?: string;
  [key: string]: unknown;
}

const ModalBase = ({
  isOpen = false,
  toggle,
  size = 'md',
  backdrop = true,
  keyboard = true,
  autoFocus = true,
  returnFocusAfterClose = true,
  unmountOnClose = true,
  fade = true,
  onOpened,
  onClosed,
  zIndex = DEFAULT_Z_INDEX,
  className,
  contentClassName,
  modalClassName,
  backdropClassName,
  backdropInvisible = false,
  labelledBy,
  role = 'dialog',
  children,
  'data-testid': dataTestId
}: ModalBaseProps): React.JSX.Element => {
  const dialogNodeRef = React.useRef<HTMLElement | null>(null);

  const triggerShake = () => {
    const node = dialogNodeRef.current;
    if (!node) {
      return;
    }
    node.setAttribute('data-shake', 'true');
    node.addEventListener('animationend', () => node.removeAttribute('data-shake'), { once: true });
  };

  const handleClose = (_event: object, reason: 'backdropClick' | 'escapeKeyDown') => {
    if (reason === 'escapeKeyDown') {
      toggle?.();
      return;
    }
    if (backdrop === 'static') {
      triggerShake();
      return;
    }
    toggle?.();
  };

  const sizeStyles = sizeCss[size] ?? sizeCss.md;

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      closeAfterTransition
      disableEscapeKeyDown={!keyboard}
      disableAutoFocus={!autoFocus}
      disableRestoreFocus={!returnFocusAfterClose}
      keepMounted={!unmountOnClose}
      hideBackdrop={backdrop === false}
      className={modalClassName}
      sx={{ zIndex, ...rootSx }}
      slotProps={{
        backdrop: {
          transitionDuration: BACKDROP_TRANSITION,
          invisible: backdropInvisible,
          className: backdropClassName
        }
      }}
    >
      <Fade
        in={isOpen}
        appear
        timeout={fade ? MODAL_TRANSITION : 0}
        onEnter={node => {
          dialogNodeRef.current = node;
        }}
        onEntered={onOpened}
        onExited={onClosed}
      >
        <div
          role={role}
          aria-modal="true"
          aria-labelledby={labelledBy}
          data-testid={dataTestId}
          css={[dialogBaseCss, sizeStyles]}
          className={className}
        >
          <div data-testid={MODAL_CONTENT_TEST_ID} css={contentBaseCss} className={contentClassName}>
            {children}
          </div>
        </div>
      </Fade>
    </Modal>
  );
};

export default ModalBase;
